import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import RelevancyGenie from './RelevancyGenie';
import { api } from '../services/api';

// Price per page for document processing
const PRICE_PER_PAGE = 1.00;

// localStorage key for saved intake drafts
const DRAFT_KEY = 'sdai_intake_draft';

const EMPTY_PROJECT_INFO = {
  name: '',
  client: '',
  address: '',
  contractor: '',
  dueDate: '',
  priority: 'normal',
  notes: ''
};

// Restore a saved draft (project info only — files cannot be persisted)
const loadDraft = () => {
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) {
      const draft = JSON.parse(saved);
      if (draft && typeof draft === 'object') {
        return { ...EMPTY_PROJECT_INFO, ...draft };
      }
    }
  } catch (error) {
    console.error('Error loading draft:', error);
  }
  return null;
};

// SHOPDRAWINGS.IO Universal File Classification System
// Based on taxonomy codes 000-700

const UPLOAD_STEPS = [
  // 100 - GOVERNANCE (The Rules) - Required
  { id: 1, code: '101', name: 'Scope of Work', category: 'GOVERNANCE', description: 'Specific Scope Narrative - defines what shop drawings are needed', required: true, accept: '.pdf,.docx,.doc', output: 'scope.json' },
  { id: 2, code: '103', name: 'Spec Sections', category: 'GOVERNANCE', description: 'Individual Spec Sections (Div 07 only) - CSI MasterFormat', required: true, accept: '.pdf', output: 'specs.json' },

  // 200 - GEOMETRY (The Shapes) - Required
  { id: 3, code: '200', name: 'Full Project Set', category: 'GEOMETRY', description: 'Combined Set (Arch+Struct+MEP) - project drawings', required: true, accept: '.pdf,.dwg,.dxf', output: 'project_files' },

  // 300 - CHEMISTRY (The Ingredients) - Required
  { id: 4, code: '301', name: 'Assembly Letter', category: 'CHEMISTRY', description: 'Manufacturer System Letter (NOA) - system components', required: true, accept: '.pdf', output: 'assembly_data.json' },

  // 300 - CHEMISTRY (Optional)
  { id: 5, code: '302', name: 'Product Data', category: 'CHEMISTRY', description: 'Cut Sheets - manufacturer catalogs with markups', required: false, accept: '.pdf', output: 'inventory_check.json' },
  { id: 6, code: '303', name: 'Manufacturer Details', category: 'CHEMISTRY', description: 'Manufacturer Standard Details → detail_crosswalk.json', required: false, accept: '.pdf', output: 'detail_crosswalk.json' },

  // 200 - GEOMETRY (Optional)
  { id: 7, code: '201', name: 'Taper Plan', category: 'GEOMETRY', description: 'Tapered Insulation Plans → slopes.json', required: false, accept: '.pdf,.dwg,.dxf', output: 'slopes.json' },
  { id: 8, code: '203', name: 'Takeoff', category: 'GEOMETRY', description: 'Edge/Stack Reports / Quantities → quantities.json', required: false, accept: '.pdf,.xlsx,.xls,.csv', output: 'quantities.json' },
  { id: 9, code: '204', name: 'Sketches', category: 'GEOMETRY', description: 'Hand sketches / Field measurements → sketch_notes.json', required: false, accept: '.pdf,.jpg,.jpeg,.png', output: 'sketch_notes.json' },

  // 100 - GOVERNANCE (Optional)
  { id: 10, code: '100', name: 'Contract Files', category: 'GOVERNANCE', description: 'Master Prime Contract / COs → risk_flags.json', required: false, accept: '.pdf,.docx,.doc', output: 'risk_flags.json' },

  // 290 - Internal Inputs (Misc)
  { id: 11, code: '290', name: 'Miscellaneous', category: 'GEOMETRY', description: 'Raw CAD / Unsorted Client Files → misc_notes.json', required: false, accept: '*', output: 'misc_notes.json' },
];

// CSI MasterFormat Division 07 Spec Codes
const CSI_DIV07_SPECS = {
  '070000': 'UNKNOWN/GENERAL',
  '071000': 'BELOW GRADE WATERPROOFING',
  '071100': 'DAMPPROOFING',
  '071300': 'SHEET WATERPROOFING',
  '071400': 'FLUID APPLIED WATERPROOFING',
  '071800': 'TRAFFIC COATINGS',
  '072500': 'WEATHER BARRIERS',
  '072700': 'AIR BARRIERS',
  '074100': 'METAL PANELS',
  '075000': 'MEMBRANE ROOFING',
  '075100': 'BUR (BUILT-UP ROOFING)',
  '075300': 'EPDM',
  '075400': 'TPO/PVC',
  '075416': 'PVC ROOFING',
  '075500': 'IRMA (PROTECTED MEMBRANE)',
  '075563': 'VEGETATED ROOFING',
  '075600': 'FLUID APPLIED ROOFING',
  '076000': 'SHEET METAL',
  '076100': 'METAL ROOFING',
  '076113': 'SSMR (STANDING SEAM)',
  '077200': 'ROOF ACCESSORIES',
  '079200': 'SEALANTS',
  '079500': 'EXPANSION JOINTS',
};

const ProjectIntakeForm = () => {
  const [projectInfo, setProjectInfo] = useState(() => loadDraft() || { ...EMPTY_PROJECT_INFO });

  const [files, setFiles] = useState({});
  const [draftSaved, setDraftSaved] = useState(false);
  const [fileMeta, setFileMeta] = useState({}); // Track page counts per file
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingStepId, setPendingStepId] = useState(null);
  const [showGenie, setShowGenie] = useState(false);
  const [totalCost, setTotalCost] = useState(0);

  const handleProjectInfoChange = (e) => {
    const { name, value } = e.target;
    setProjectInfo(prev => ({ ...prev, [name]: value }));
  };

  // Save project info as a draft in localStorage (files cannot be persisted)
  const handleSaveDraft = () => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(projectInfo));
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 2000);
    } catch (error) {
      alert(`Error saving draft: ${error.message}`);
    }
  };

  // Count pages in PDF using pdf-lib
  const countPdfPages = async (file) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      return 1; // Non-PDF files count as 1 page
    }

    try {
      const { PDFDocument } = await import('pdf-lib');
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      return pdf.getPageCount();
    } catch (error) {
      console.error('Error counting pages:', error);
      return 1;
    }
  };

  // Handle file drop - check page count and show pricing modal
  const handleFileDropped = async (stepId, newFiles) => {
    for (const file of newFiles) {
      const pageCount = await countPdfPages(file);
      const cost = pageCount * PRICE_PER_PAGE;

      // If file has more than 5 pages, show pricing modal
      if (pageCount > 5) {
        setPendingFile({ file, pageCount, cost });
        setPendingStepId(stepId);
        setShowPricingModal(true);
        return; // Handle one file at a time for large files
      } else {
        // Small files just get added
        addFileWithMeta(stepId, file, pageCount);
      }
    }
  };

  // Add file with metadata
  const addFileWithMeta = (stepId, file, pageCount, filteredPages = null) => {
    const fileId = `${stepId}-${Date.now()}-${file.name}`;

    setFiles(prev => ({
      ...prev,
      [stepId]: [...(prev[stepId] || []), { file, id: fileId }]
    }));

    setFileMeta(prev => ({
      ...prev,
      [fileId]: {
        pageCount,
        filteredPages,
        cost: pageCount * PRICE_PER_PAGE,
        wasFiltered: filteredPages !== null
      }
    }));

    // Update total cost
    setTotalCost(prev => prev + (pageCount * PRICE_PER_PAGE));
  };

  // Accept full file with pricing
  const acceptFullFile = () => {
    if (pendingFile && pendingStepId) {
      addFileWithMeta(pendingStepId, pendingFile.file, pendingFile.pageCount);
      setShowPricingModal(false);
      setPendingFile(null);
      setPendingStepId(null);
    }
  };

  // Open Relevancy Genie for filtering
  const openGenie = () => {
    setShowPricingModal(false);
    setShowGenie(true);
  };

  // Handle Genie completion
  const handleGenieComplete = (filteredBlob, selectedPages, originalPageCount) => {
    if (pendingStepId) {
      // Create a new File from the filtered blob
      const filteredFile = new File(
        [filteredBlob],
        `filtered_${pendingFile.file.name}`,
        { type: 'application/pdf' }
      );

      addFileWithMeta(pendingStepId, filteredFile, selectedPages.length, selectedPages);
      setShowGenie(false);
      setPendingFile(null);
      setPendingStepId(null);
    }
  };

  const removeFile = (stepId, fileIndex) => {
    const fileData = files[stepId][fileIndex];
    const meta = fileMeta[fileData.id];

    setFiles(prev => ({
      ...prev,
      [stepId]: prev[stepId].filter((_, i) => i !== fileIndex)
    }));

    // Remove from meta and update cost
    setFileMeta(prev => {
      const newMeta = { ...prev };
      delete newMeta[fileData.id];
      return newMeta;
    });

    if (meta) {
      setTotalCost(prev => prev - meta.cost);
    }
  };

  const handleSubmit = async () => {
    // Validate required files
    const missingRequired = UPLOAD_STEPS
      .filter(step => step.required)
      .filter(step => !files[step.id] || files[step.id].length === 0);

    if (missingRequired.length > 0) {
      alert(`Please upload files for: ${missingRequired.map(s => s.name).join(', ')}`);
      return;
    }

    if (!projectInfo.name || !projectInfo.client) {
      alert('Please enter project name and client');
      return;
    }

    setIsSubmitting(true);

    try {
      // First create the project (api client adds base URL + auth token;
      // the backend links the project to the caller's Client record)
      const projectResponse = await api.post('/api/projects', {
        name: projectInfo.name,
        client_name: projectInfo.client,
        description: projectInfo.notes,
        address: projectInfo.address,
        general_contractor: projectInfo.contractor,
        due_date: projectInfo.dueDate,
        priority: projectInfo.priority,
        quoted_amount: totalCost,
        notes: projectInfo.notes
      });

      if (!projectResponse.success) {
        throw new Error(projectResponse.error || 'Failed to create project');
      }

      const project = projectResponse.data;

      // Then upload files for each step
      for (const [stepId, stepFiles] of Object.entries(files)) {
        const step = UPLOAD_STEPS.find(s => s.id === parseInt(stepId));

        for (const fileData of stepFiles) {
          const meta = fileMeta[fileData.id];
          const uploadResponse = await api.uploadFile(
            `/api/projects/${project.id}/documents`,
            fileData.file,
            {
              taxonomy_code: step.code,
              taxonomy_category: step.category,
              step_name: step.name,
              output_target: step.output,
              ...(meta ? {
                page_count: meta.pageCount,
                was_filtered: meta.wasFiltered,
                ...(meta.filteredPages ? { selected_pages: meta.filteredPages } : {})
              } : {})
            }
          );
          if (!uploadResponse.success) {
            throw new Error(uploadResponse.error || `Failed to upload ${fileData.file.name}`);
          }
        }
      }

      alert(`Project created! ID: ${project.project_number}\nTotal processing cost: $${totalCost.toFixed(2)}`);

      // Reset form and clear any saved draft
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch (e) {
        console.error('Error clearing draft:', e);
      }
      setProjectInfo({ ...EMPTY_PROJECT_INFO });
      setFiles({});
      setFileMeta({});
      setTotalCost(0);

    } catch (error) {
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const requiredComplete = UPLOAD_STEPS
    .filter(s => s.required)
    .every(s => files[s.id] && files[s.id].length > 0);

  // Calculate total pages
  const totalPages = Object.values(fileMeta).reduce((sum, meta) => sum + meta.pageCount, 0);

  return (
    <div className="intake-form">
      {/* Pricing Modal */}
      {showPricingModal && pendingFile && (
        <div className="modal-overlay">
          <div className="pricing-modal">
            <h2>📄 Large File Detected</h2>
            <div className="file-info">
              <span className="file-name">{pendingFile.file.name}</span>
              <span className="page-count">{pendingFile.pageCount} pages</span>
            </div>

            <div className="pricing-breakdown">
              <div className="price-line">
                <span>Processing Fee ({pendingFile.pageCount} pages × ${PRICE_PER_PAGE})</span>
                <span className="price">${pendingFile.cost.toFixed(2)}</span>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn primary large" onClick={acceptFullFile}>
                💳 PROCESS FULL DOCUMENT (${pendingFile.cost.toFixed(2)})
              </button>
              <p className="subtext">Includes AI Indexing, Vector Parsing, and Cloud Storage</p>

              <div className="divider">or</div>

              <button className="btn secondary" onClick={openGenie}>
                🧙 Open Scope Definition Wizard (Filter Pages)
              </button>
              <p className="subtext muted">Select only relevant pages to reduce processing costs</p>
            </div>

            <button className="close-btn" onClick={() => {
              setShowPricingModal(false);
              setPendingFile(null);
              setPendingStepId(null);
            }}>✕</button>
          </div>
        </div>
      )}

      {/* Relevancy Genie Modal */}
      {showGenie && pendingFile && (
        <div className="modal-overlay">
          <div className="genie-modal">
            <button className="close-btn" onClick={() => {
              setShowGenie(false);
              setPendingFile(null);
              setPendingStepId(null);
            }}>✕</button>
            <RelevancyGenie
              file={pendingFile.file}
              onComplete={handleGenieComplete}
              onSkip={acceptFullFile}
              onCancel={() => {
                setShowGenie(false);
                setPendingFile(null);
                setPendingStepId(null);
              }}
            />
          </div>
        </div>
      )}

      <div className="form-header">
        <h2>📤 New Project Intake</h2>
        <p>Upload your project documents for shop drawing production</p>

        {/* Running Cost Display */}
        <div className="cost-tracker">
          <span className="cost-label">Processing Cost:</span>
          <span className="cost-value">${totalCost.toFixed(2)}</span>
          <span className="page-count">({totalPages} pages)</span>
        </div>
      </div>

      {/* Project Information */}
      <div className="form-section">
        <h3>📋 Project Information</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Project Name *</label>
            <input
              type="text"
              name="name"
              value={projectInfo.name}
              onChange={handleProjectInfoChange}
              placeholder="e.g., Walmart Distribution Center - Atlanta"
              required
            />
          </div>
          <div className="form-group">
            <label>Client/Company *</label>
            <input
              type="text"
              name="client"
              value={projectInfo.client}
              onChange={handleProjectInfoChange}
              placeholder="e.g., ABC Roofing Co."
              required
            />
          </div>
          <div className="form-group">
            <label>Project Address</label>
            <input
              type="text"
              name="address"
              value={projectInfo.address}
              onChange={handleProjectInfoChange}
              placeholder="e.g., 123 Main St, Atlanta, GA 30301"
            />
          </div>
          <div className="form-group">
            <label>General Contractor</label>
            <input
              type="text"
              name="contractor"
              value={projectInfo.contractor}
              onChange={handleProjectInfoChange}
              placeholder="e.g., Turner Construction"
            />
          </div>
          <div className="form-group">
            <label>Due Date</label>
            <input
              type="date"
              name="dueDate"
              value={projectInfo.dueDate}
              onChange={handleProjectInfoChange}
            />
          </div>
          <div className="form-group">
            <label>Priority</label>
            <select
              name="priority"
              value={projectInfo.priority}
              onChange={handleProjectInfoChange}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High (Rush +50%)</option>
              <option value="urgent">Urgent (24hr +100%)</option>
            </select>
          </div>
        </div>
        <div className="form-group full-width">
          <label>Additional Notes</label>
          <textarea
            name="notes"
            value={projectInfo.notes}
            onChange={handleProjectInfoChange}
            placeholder="Any special instructions or requirements..."
            rows={3}
          />
        </div>
      </div>

      {/* File Upload Steps */}
      <div className="upload-steps">
        <div className="steps-header">
          <h3>📁 Document Upload</h3>
          <div className="steps-legend">
            <span className="required-badge">Required</span>
            <span className="optional-badge">Optional</span>
          </div>
        </div>

        {/* Required Section */}
        <div className="upload-section">
          <h4>Required Documents</h4>
          <div className="upload-grid">
            {UPLOAD_STEPS.filter(s => s.required).map(step => (
              <UploadZone
                key={step.id}
                step={step}
                files={files[step.id] || []}
                fileMeta={fileMeta}
                onFilesChange={(newFiles) => handleFileDropped(step.id, newFiles)}
                onRemoveFile={(index) => removeFile(step.id, index)}
              />
            ))}
          </div>
        </div>

        {/* Optional Section */}
        <div className="upload-section optional">
          <h4>Optional Documents (Helpful but not required)</h4>
          <div className="upload-grid">
            {UPLOAD_STEPS.filter(s => !s.required).map(step => (
              <UploadZone
                key={step.id}
                step={step}
                files={files[step.id] || []}
                fileMeta={fileMeta}
                onFilesChange={(newFiles) => handleFileDropped(step.id, newFiles)}
                onRemoveFile={(index) => removeFile(step.id, index)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Submit Section */}
      <div className="submit-section">
        <div className="validation-status">
          {requiredComplete ? (
            <span className="status-complete">✅ All required documents uploaded</span>
          ) : (
            <span className="status-incomplete">⚠️ Please upload all required documents</span>
          )}
        </div>

        <div className="cost-summary">
          <div className="summary-line">
            <span>Document Processing ({totalPages} pages)</span>
            <span>${totalCost.toFixed(2)}</span>
          </div>
          {projectInfo.priority === 'high' && (
            <div className="summary-line rush">
              <span>Rush Fee (+50%)</span>
              <span>+${(totalCost * 0.5).toFixed(2)}</span>
            </div>
          )}
          {projectInfo.priority === 'urgent' && (
            <div className="summary-line rush">
              <span>Urgent Fee (+100%)</span>
              <span>+${totalCost.toFixed(2)}</span>
            </div>
          )}
          <div className="summary-total">
            <span>Total Due Now</span>
            <span className="total-price">
              ${(totalCost * (projectInfo.priority === 'high' ? 1.5 : projectInfo.priority === 'urgent' ? 2 : 1)).toFixed(2)}
            </span>
          </div>
        </div>

        <div className="submit-actions">
          <button className="btn secondary" onClick={handleSaveDraft}>
            {draftSaved ? '✅ Draft Saved' : '💾 Save Draft'}
          </button>
          <button
            className="btn primary"
            onClick={handleSubmit}
            disabled={!requiredComplete || isSubmitting}
          >
            {isSubmitting ? '⏳ Processing...' : `💳 Submit & Pay $${(totalCost * (projectInfo.priority === 'high' ? 1.5 : projectInfo.priority === 'urgent' ? 2 : 1)).toFixed(2)}`}
          </button>
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .pricing-modal {
          background: var(--bg-secondary, #1e293b);
          padding: 32px;
          border-radius: 16px;
          max-width: 500px;
          width: 90%;
          position: relative;
        }

        .pricing-modal h2 {
          margin: 0 0 16px 0;
          text-align: center;
        }

        .file-info {
          display: flex;
          justify-content: space-between;
          padding: 16px;
          background: var(--bg-tertiary, #334155);
          border-radius: 8px;
          margin-bottom: 24px;
        }

        .file-name {
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .page-count {
          color: var(--text-muted, #94a3b8);
        }

        .pricing-breakdown {
          margin-bottom: 24px;
        }

        .price-line {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
        }

        .price {
          font-weight: 700;
          font-size: 1.2em;
          color: var(--success, #10b981);
        }

        .modal-actions {
          text-align: center;
        }

        .modal-actions .btn.large {
          width: 100%;
          padding: 16px;
          font-size: 1.1em;
        }

        .subtext {
          font-size: 0.85em;
          color: var(--text-muted, #94a3b8);
          margin: 8px 0;
        }

        .subtext.muted {
          opacity: 0.7;
        }

        .divider {
          margin: 16px 0;
          color: var(--text-muted, #94a3b8);
        }

        .close-btn {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: var(--text-muted, #94a3b8);
        }

        .genie-modal {
          background: var(--bg-secondary, #1e293b);
          padding: 24px;
          border-radius: 16px;
          max-width: 900px;
          width: 95%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
        }

        .cost-tracker {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: var(--bg-tertiary, #334155);
          padding: 8px 16px;
          border-radius: 8px;
          margin-top: 16px;
        }

        .cost-value {
          font-weight: 700;
          font-size: 1.2em;
          color: var(--success, #10b981);
        }

        .cost-summary {
          background: var(--bg-tertiary, #334155);
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 16px;
        }

        .summary-line {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          border-bottom: 1px solid var(--border-color, #475569);
        }

        .summary-line.rush {
          color: var(--warning, #f59e0b);
        }

        .summary-total {
          display: flex;
          justify-content: space-between;
          padding: 16px 0 0 0;
          font-weight: 700;
          font-size: 1.2em;
        }

        .total-price {
          color: var(--success, #10b981);
        }

        .file-cost {
          font-size: 0.85em;
          color: var(--success, #10b981);
          font-weight: 600;
        }

        .filtered-badge {
          font-size: 0.75em;
          background: var(--primary, #2563eb);
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
          margin-left: 8px;
        }
      `}</style>
    </div>
  );
};

// Upload Zone Component
const UploadZone = ({ step, files, fileMeta, onFilesChange, onRemoveFile }) => {
  const onDrop = useCallback((acceptedFiles) => {
    onFilesChange(acceptedFiles);
  }, [onFilesChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: step.accept === '*' ? undefined : step.accept.split(',').reduce((acc, ext) => {
      const mimeTypes = {
        '.pdf': 'application/pdf',
        '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.doc': 'application/msword',
        '.dwg': 'application/acad',
        '.dxf': 'application/dxf',
        '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        '.xls': 'application/vnd.ms-excel',
        '.csv': 'text/csv',
        '.jpg': 'image/jpeg',
        '.jpeg': 'image/jpeg',
        '.png': 'image/png'
      };
      if (mimeTypes[ext.trim()]) {
        acc[mimeTypes[ext.trim()]] = [ext.trim()];
      }
      return acc;
    }, {}),
    multiple: true
  });

  const hasFiles = files.length > 0;

  return (
    <div className={`upload-zone ${step.required ? 'required' : 'optional'} ${hasFiles ? 'has-files' : ''}`}>
      <div className="zone-header">
        <span className="taxonomy-code">{step.code}</span>
        <span className="taxonomy-category">{step.category}</span>
        <span className={`badge ${step.required ? 'required' : 'optional'}`}>
          {step.required ? 'Required' : 'Optional'}
        </span>
      </div>
      <h5>{step.name}</h5>
      <p className="zone-description">{step.description}</p>
      <p className="zone-output">→ {step.output}</p>

      <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>📥 Drop files here...</p>
        ) : (
          <p>
            <span className="upload-icon">📎</span>
            Drag & drop or click to upload
            <span className="file-types">{step.accept}</span>
          </p>
        )}
      </div>

      {files.length > 0 && (
        <div className="file-list">
          {files.map((fileData, index) => {
            const meta = fileMeta[fileData.id];
            return (
              <div key={index} className="file-item">
                <span className="file-icon">📄</span>
                <span className="file-name">{fileData.file.name}</span>
                {meta && (
                  <>
                    <span className="file-pages">{meta.pageCount} pg</span>
                    <span className="file-cost">${meta.cost.toFixed(2)}</span>
                    {meta.wasFiltered && <span className="filtered-badge">Filtered</span>}
                  </>
                )}
                <button
                  className="remove-btn"
                  onClick={() => onRemoveFile(index)}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        .taxonomy-code {
          font-size: 12px;
          font-weight: 700;
          background: var(--primary, #2563eb);
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-family: monospace;
        }

        .taxonomy-category {
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          background: var(--bg-tertiary, #334155);
          padding: 2px 6px;
          border-radius: 4px;
          margin-left: 8px;
        }

        .taxonomy-category:contains("GOVERNANCE") { background: #7c3aed; }
        .taxonomy-category:contains("GEOMETRY") { background: #059669; }
        .taxonomy-category:contains("CHEMISTRY") { background: #d97706; }

        .zone-output {
          font-size: 11px;
          color: var(--success, #10b981);
          font-family: monospace;
          margin-top: 4px;
        }

        .file-pages {
          font-size: 0.85em;
          color: var(--text-muted, #94a3b8);
          margin-left: auto;
        }
      `}</style>
    </div>
  );
};

export default ProjectIntakeForm;
